// DEPLOY: /var/www/woody.wtf/frontend/src/chart/InfoBar.js
// Company strip under the chart controls (from /chart/info, same fields as the old chart's
// info bar): name | sector | industry, then valuation | profitability | risk | events | website,
// plus the analyst summary (AnalystSummary) and the chart's actions (+ List, Auto patterns,
// Company page). Collapsible: open by default on desktop, closed on phones (one tap to open);
// the choice is remembered on this device.
import React, { useState } from 'react';
import AnalystSummary from './AnalystSummary';

const S = {
  bar:    { background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '5px 16px', display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 },
  head:   { display: 'flex', alignItems: 'center', gap: '10px', minHeight: '26px' },
  toggle: { border: 'none', background: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: 1, textAlign: 'left', color: 'var(--text-muted)', fontSize: '12px' },
  ident:  { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  caret:  { fontSize: '10px', color: 'var(--accent)', flexShrink: 0 },
  acts:   { display: 'flex', gap: '6px', flexShrink: 0 },
  act:    { background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '3px 10px', fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' },
  nums:   { display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: '14px', rowGap: '4px' },
  item:   { display: 'inline-flex', gap: '5px', alignItems: 'baseline', whiteSpace: 'nowrap' },
  divider:{ width: '1px', height: '12px', background: 'var(--border)', alignSelf: 'center' },
  label:  { fontSize: '10px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.04em' },
  value:  { fontSize: '12px', fontWeight: 600, color: 'var(--text)' },
  link:   { fontSize: '12px', fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' },
};

const CSS = `
@media (max-width: 768px) {
  .c2-info { padding: 5px 10px !important; }
  .c2-info-acts button { padding: 3px 7px !important; font-size: 11px !important; }
}`;

// ── Formatting (same rules as the old chart's info bar) ──
function fmt(v, dp = 2) {
  if (v == null || v === '') return null;
  return Number(v).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}
function fmtLarge(v) {
  if (!v) return null;
  if (v >= 1e12) return '$' + fmt(v / 1e12, 2) + 'T';
  if (v >= 1e9) return '$' + fmt(v / 1e9, 2) + 'B';
  if (v >= 1e6) return '$' + fmt(v / 1e6, 2) + 'M';
  return '$' + fmt(v, 0);
}
const fmtPct = v => (v != null ? fmt(v * 100, 2) + '%' : null);
const fmtDay = d => {
  if (!d) return null;
  const t = new Date(d + 'T00:00:00');
  if (isNaN(t)) return d;
  const opts = { day: 'numeric', month: 'short' };
  if (t.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return t.toLocaleDateString('en-AU', opts);
};
const known = v => v != null && v !== '' && v !== 'Unknown';

function Groups({ groups }) {
  const live = groups.map(g => g.filter(it => known(it.value))).filter(g => g.length);
  return live.map((g, gi) => (
    <React.Fragment key={gi}>
      {gi > 0 && <span style={S.divider} />}
      {g.map(it => it.href
        ? <a key={it.label} href={it.href} target="_blank" rel="noopener noreferrer" style={S.link}>{it.value}</a>
        : <span key={it.label} style={S.item}><span style={S.label}>{it.label}</span><span style={S.value}>{it.value}</span></span>)}
    </React.Fragment>
  ));
}

const readOpen = mobile => { try { const v = localStorage.getItem('chart2InfoOpen'); return v === null ? !mobile : v === 'true'; } catch (_) { return !mobile; } };

export default function InfoBar({ info, isRatio, futures, ticker, mobile, analyst, price, onAddList, onPatterns, patternsOn }) {
  const [open, setOpen] = useState(() => readOpen(mobile));
  const toggle = () => setOpen(o => { try { localStorage.setItem('chart2InfoOpen', String(!o)); } catch (_) {} return !o; });

  let ident = isRatio ? `Ratio ${ticker}` : ticker, groups = [];
  const index = info?.asset_type === 'index';     // indexes: no lists, no company page
  if (!isRatio && info) {
    const { name, asset_type, fundamentals: f = {}, snapshot: snap = {}, profile: p = {} } = info;
    const isETF = ['ETF', 'etf'].includes(asset_type);
    const web = p.website_url && { label: 'WEB', href: p.website_url, value: p.website_url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '') };
    let idParts;
    if (index) idParts = [];            // an index: just its name, no fundamentals to show
    else if (isETF) idParts = [p.sector, f.provider, f.index_tracked];
    else {
      const gics = [...new Set([p.gics_sector, p.gics_group].filter(known))];
      const own = known(p.industry) && !gics.includes(p.industry) ? ` (${p.industry})` : '';
      idParts = [gics.length ? gics.join(' | ') + own : [p.sector, p.industry].filter(known).join(' | ')];
    }
    ident = [name, ...idParts].filter(known).join(' | ') || ticker;
    groups = index ? [] : isETF ? [
      [{ label: 'MER', value: f.expense_ratio != null ? fmt(f.expense_ratio * 100, 2) + '%' : null },
       { label: 'AUM', value: fmtLarge(f.aum_usd) }, { label: 'YIELD', value: fmtPct(f.ttm_yield) }],
      [{ label: 'RSI', value: snap.rsi14 ? fmt(snap.rsi14, 1) : null }],
      [web || {}],
    ] : [
      [{ label: 'MKT CAP', value: fmtLarge(f.market_cap) }, { label: 'P/E', value: fmt(f.pe_ratio, 1) },
       { label: 'P/B', value: fmt(f.price_to_book, 1) }, { label: 'EPS', value: fmt(f.eps_ttm) },
       { label: 'YIELD', value: fmtPct(f.dividend_yield) }],
      [{ label: 'MARGIN', value: fmtPct(f.profit_margin) }, { label: 'ROE', value: fmtPct(f.return_on_equity_ttm) }],
      [{ label: 'BETA', value: fmt(f.beta) }, { label: 'RSI', value: snap.rsi14 ? fmt(snap.rsi14, 1) : null }],
      [{ label: 'NEXT EARN', value: fmtDay(f.next_earnings_date) },
       { label: 'TARGET', value: f.wall_street_target_price != null ? '$' + fmt(f.wall_street_target_price) : null }],
      [web || {}],
    ];
  }

  return (
    <div className="c2-info" style={S.bar}>
      <style>{CSS}</style>
      <div style={S.head}>
        <button style={S.toggle} onClick={toggle} title={open ? 'Hide company info' : 'Show company info'}>
          <span style={S.caret}>{open ? '▾' : '▸'}</span>
          <span style={S.ident}>{ident}</span>
        </button>
        <div className="c2-info-acts" style={S.acts}>
          {!isRatio && !futures && !index && <button style={S.act} onClick={onAddList} title="Add to a list">+ List</button>}
          {!isRatio && (
            <button style={{ ...S.act, ...(patternsOn ? { color: 'var(--accent)', borderColor: 'var(--accent)' } : {}) }}
                    onClick={onPatterns} title="Rule-based chart pattern recognition">
              {mobile ? 'Patterns' : 'Auto patterns'} {patternsOn ? 'on' : 'off'}
            </button>
          )}
          {!isRatio && !futures && !index && (
            <button style={S.act} title="Open the company page in a new tab"
                    onClick={() => window.open(`/company?ticker=${encodeURIComponent(ticker)}`, '_blank', 'noopener')}>Company ↗</button>
          )}
        </div>
      </div>
      {open && !isRatio && !futures && (
        <>
          {groups.length > 0 && <div style={S.nums}><Groups groups={groups} /></div>}
          <AnalystSummary data={analyst} price={price} />
        </>
      )}
    </div>
  );
}
